import React from 'react'
import { useApolloClient, useSubscription, useQuery } from '@apollo/react-hooks'
import { LANGUAGES_LIST, LANGUAGE_TRANSLATIONS, BRAND } from '../graphql'
import en from '../lang/en.json'
import fr from '../lang/fr.json'
import ar from '../lang/ar.json'
import NL from '../lang/nl.json'
import { IntlProvider, FormattedMessage, useIntl } from 'react-intl'
import { isClient, isKiosk } from '../utils'
import { Loader } from '../components'
import { useConfig } from '../lib'
const LanguageContext = React.createContext()

const languages = { en, fr, ar, NL }
const rltLanguages = ['ar'] //Languages are read from right to left

export const LanguageProvider = ({ children }) => {
   const apolloCLient = useApolloClient()
   const { brand, brandLocation } = useConfig()
   const [languageJSON, setLanguageJSON] = React.useState({})
   const [languageJSONLoading, setLanguageJSONLoading] = React.useState(true)
   const [locales, setLocales] = React.useState()

   const {
      loading,
      data: languagesList,
      error,
   } = useQuery(BRAND, {
      variables: {
         brandCondition: {
            id: {
               _eq: brand.id,
            },
         },
         ...(brandLocation?.id && {
            brandLocationCondition: {
               id: { _eq: brandLocation?.id },
            },
         }),
      },
      onCompleted: data => {
         if (data.brands) {
            if (brandLocation?.id) {
               setLocales(
                  data?.brands[0]?.brand_locations[0]?.selectedLanguages
               )
            } else {
               setLocales(data.brands[0].selectedLanguages)
            }
         }
      },
   })
   const [locale, changeLocale] = React.useState()
   const [direction, changeDirection] = React.useState()

   const setLangTrans = async locale => {
      setLanguageJSONLoading(true)
      const { data } = await apolloCLient.query({
         query: LANGUAGE_TRANSLATIONS,
         variables: {
            where: {
               language: {
                  code: {
                     _ilike: locale?.toUpperCase() || locale?.toLowerCase(),
                  },
               },
            },
         },
      })
      if (data?.master_languageTranslation?.length > 0) {
         const translationArray = data.master_languageTranslation
         const newLanguageJSON = {}
         for (let ele of translationArray) {
            newLanguageJSON[ele.label] = ele.translationValue
         }
         setLanguageJSON(newLanguageJSON)
      } else setLanguageJSON({})
      setLanguageJSONLoading(false)
   }

   React.useEffect(() => {
      if (locales) {
         changeLocale(locales?.find(x => x.default)?.code)
         changeDirection(locales?.find(x => x.default)?.direction)
         for (let language of locales) {
            if (language.direction === 'rtl') {
               rltLanguages.push(language.code)
            }
         }
      }
   }, [locales])

   //Change direction for right to left read languages
   React.useEffect(() => {
      // JSON.parse(localStorage.getItem('language'))
      if (rltLanguages.includes(locale)) {
         changeDirection('rtl')
      } else {
         changeDirection('ltr')
      }

      if (locale) {
         setLangTrans(locale)
      }
   }, [locale])

   const kiosk = isKiosk()

   React.useEffect(() => {
      if (!kiosk) {
         const languageInLocal = isClient
            ? window.localStorage.getItem('language')
            : ''
         if (languageInLocal) changeLocale(languageInLocal)
         else changeLocale('EN')
      }
   }, [])
   if (loading || !locale) return <Loader type="brand-loading" />

   return (
      <LanguageContext.Provider
         value={{ locale, changeLocale, direction, changeDirection, locales }}
      >
         {Object.keys(languageJSON).length > 0 ? (
            <IntlProvider
               locale={locale}
               messages={languageJSON}
               onError={error => {}}
            >
               {children}
            </IntlProvider>
         ) : (
            <IntlProvider
               locale={locale}
               messages={
                  languages[locale?.toLowerCase()] ||
                  languages[locale?.toUpperCase()]
               }
               onError={error => {}}
            >
               {children}
            </IntlProvider>
         )}
      </LanguageContext.Provider>
   )
}

export const useTranslation = () => {
   const intl = useIntl()
   const { locale, changeLocale, direction, changeDirection, locales } =
      React.useContext(LanguageContext)

   const t = text => {
      if (text) {
         return <FormattedMessage id={text} />
      } else return text
   }

   const dynamicTrans = (langTags, placeholder) => {
      langTags.forEach(tag => {
         let langPattern
         if (tag.hasAttribute('data-original-value')) {
            langPattern = tag.getAttribute('data-original-value')
         } else {
            if (placeholder) {
               langPattern = tag.placeholder
            } else {
               langPattern = tag.innerHTML
            }
            tag.setAttribute('data-original-value', langPattern)
         }
         let innerHTMLToBe = ''

         const languageSelected = locale?.toUpperCase()
         if (languageSelected != 'EN') {
            let re = new RegExp(
               `\@@${languageSelected}@@(.*?)\@@${languageSelected}@@`,
               'g'
            )
            if (langPattern && langPattern.match(re))
               innerHTMLToBe = langPattern
                  .match(re)[0]
                  .replaceAll(`@@${languageSelected}@@`, '')
         }

         if (
            innerHTMLToBe == '' &&
            langPattern.split('@@')[0] != '' &&
            !(
               languageSelected == 'EN' ||
               languageSelected.toLowerCase() == 'en'
            )
         ) {
            innerHTMLToBe = intl.formatMessage({
               id: langPattern.split('@@')[0],
               defaultMessage: langPattern.split('@@')[0],
            })
         } else if (innerHTMLToBe == '') {
            innerHTMLToBe = langPattern.split('@@')[0]
         }
         if (
            innerHTMLToBe == '' ||
            languageSelected == 'EN' ||
            languageSelected.toLowerCase() == 'en'
         ) {
            innerHTMLToBe = langPattern.split('@@')[0]
         }
         if (placeholder) {
            tag.placeholder = innerHTMLToBe
         } else {
            tag.innerHTML = innerHTMLToBe
         }
      })
   }

   const currentLang = React.useMemo(() => locale, [locale])

   React.useEffect(() => {
      const languageTags = document.querySelectorAll(
         '[data-translation="true"]'
      )
      dynamicTrans(languageTags)
   }, [currentLang])

   return {
      locales,
      locale,
      changeLocale,
      direction,
      changeDirection,
      t,
      dynamicTrans,
   }
}
